import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Smile, Zap, Activity, BookOpen, Share2, ClipboardList, Building2,
  Plus, RefreshCw, X, CheckCircle2, AlertTriangle, Trash2, Send, DollarSign,
} from 'lucide-react';
import { theme, cardStyle, btnStyle, inputStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  CLINICAL — Phase 6 hub:
//  Ortho · Implants · Perio · Education · Referrals · TreatmentPlans · MultiBranch
// ═══════════════════════════════════════════════════════════════════

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : '—';
const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// FDI upper: 18-11, 21-28; lower: 48-41, 31-38
const FDI_UPPER = ['18','17','16','15','14','13','12','11','21','22','23','24','25','26','27','28'];
const FDI_LOWER = ['48','47','46','45','44','43','42','41','31','32','33','34','35','36','37','38'];

const ORTHO_STATUS_COLOR = { planning:'#6b7280', active:'#3b82f6', refinement:'#8b5cf6', retention:'#06b6d4', completed:'#16a34a', abandoned:'#dc2626' };
const IMPLANT_STATUS_COLOR = { planned:'#6b7280', placed:'#3b82f6', integrated:'#06b6d4', loaded:'#16a34a', failed:'#dc2626', explanted:'#dc2626' };
const REFERRAL_STATUS_COLOR = { created:'#6b7280', sent:'#3b82f6', accepted:'#06b6d4', scheduled:'#8b5cf6', completed:'#16a34a', declined:'#dc2626', lost:'#dc2626' };
const PLAN_STATUS_COLOR = { draft:'#6b7280', presented:'#3b82f6', accepted:'#06b6d4', in_progress:'#f59e0b', completed:'#16a34a', declined:'#dc2626', archived:'#9ca3af' };

// initialTab accepts the internal tab keys (ortho/implants/perio/edu/referrals/
// plans/branches) and the legacy hub keys (ortho/perio/implants/education) that
// previously routed to GenericCrudPage stubs in ClinicalPagesBundle.jsx.
const CLINICAL_TAB_MAP = {
  ortho:     'ortho',
  perio:     'perio',
  implants:  'implants',
  education: 'edu',
  edu:       'edu',
  referrals: 'referrals',
  plans:     'plans',
  branches:  'branches',
};

// When `lockedPatient` is provided (used inside PatientProfile), the page
// hides its own header/picker, locks selPatient to that patient, and drops
// the cross-patient "Multi-Branch" tab.
const ClinicalPage = ({ API_BASE, currentUser, initialTab = 'ortho', lockedPatient = null }) => {
  const [tab, setTab] = useState(CLINICAL_TAB_MAP[initialTab] || 'ortho');
  const [patients, setPatients] = useState([]);
  const [picker, setPicker] = useState('');
  const [selPatient, setSelPatient] = useState(lockedPatient);
  const [msg, setMsg] = useState({ type: '', text: '' });
  const flash = (t, x) => { setMsg({ type: t, text: x }); setTimeout(() => setMsg({ type: '', text: '' }), 3500); };

  useEffect(() => { axios.get(`${API_BASE}/patients`).then(r => setPatients(r.data || [])).catch(()=>{}); }, [API_BASE]);
  const patientMatches = picker
    ? patients.filter(p => (p.name || '').toLowerCase().includes(picker.toLowerCase()) || (p.phone || '').includes(picker) || (p.uhid || '').toLowerCase().includes(picker.toLowerCase())).slice(0, 8)
    : [];

  // Ortho
  const [orthoCases, setOrthoCases] = useState([]);
  const [newOrtho, setNewOrtho] = useState({ caseType: 'aligners', brand: '', totalTrays: 0, estimatedCost: 0, chiefComplaint: '' });

  // Implants
  const [implants, setImplants] = useState([]);
  const [newImplant, setNewImplant] = useState({ toothNumber: '', brand: '', diameter: 4.0, length: 10, lotNumber: '', surgeryDate: '', warrantyYears: 10 });

  // Perio
  const [perioCharts, setPerioCharts] = useState([]);
  const [perioDraft, setPerioDraft] = useState(null);

  // Education
  const [eduItems, setEduItems] = useState([]);
  const [newEdu, setNewEdu] = useState({ title: '', category: 'general', mediaType: 'link', externalUrl: '', forTreatment: '' });

  // Referrals
  const [referrals, setReferrals] = useState([]);
  const [refStats, setRefStats] = useState(null);
  const [newRef, setNewRef] = useState({ direction: 'outbound', specialistName: '', specialty: 'oral_surgery', reason: '', urgency: 'routine' });

  // Treatment plans
  const [plans, setPlans] = useState([]);
  const [newPlan, setNewPlan] = useState({ planName: '', chiefComplaint: '', diagnosis: '' });
  const [expandedPlan, setExpandedPlan] = useState(null);

  // Multi-branch
  const [consolidation, setConsolidation] = useState(null);
  const [compareRows, setCompareRows] = useState([]);

  const reloadOrtho = useCallback(async () => {
    try { const { data } = await axios.get(`${API_BASE}/ortho`, { params: selPatient ? { patientId: selPatient._id } : {} }); setOrthoCases(data || []); } catch {}
  }, [API_BASE, selPatient]);
  const reloadImplants = useCallback(async () => {
    try { const { data } = await axios.get(`${API_BASE}/implants`, { params: selPatient ? { patientId: selPatient._id } : {} }); setImplants(data || []); } catch {}
  }, [API_BASE, selPatient]);
  const reloadPerio = useCallback(async () => {
    try { const { data } = await axios.get(`${API_BASE}/perio`, { params: selPatient ? { patientId: selPatient._id } : {} }); setPerioCharts(data || []); } catch {}
  }, [API_BASE, selPatient]);
  const reloadEdu = useCallback(async () => {
    try { const { data } = await axios.get(`${API_BASE}/education`); setEduItems(data || []); } catch {}
  }, [API_BASE]);
  const reloadReferrals = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([axios.get(`${API_BASE}/referrals`), axios.get(`${API_BASE}/referrals/stats`)]);
      setReferrals(l.data || []); setRefStats(s.data);
    } catch {}
  }, [API_BASE]);
  const reloadPlans = useCallback(async () => {
    try { const { data } = await axios.get(`${API_BASE}/treatment-plans`, { params: selPatient ? { patientId: selPatient._id } : {} }); setPlans(data || []); } catch {}
  }, [API_BASE, selPatient]);
  const reloadMultiBranch = useCallback(async () => {
    try {
      const [c, cmp] = await Promise.all([
        axios.get(`${API_BASE}/multi-branch/consolidation?days=30`),
        axios.get(`${API_BASE}/multi-branch/compare?days=30`),
      ]);
      setConsolidation(c.data); setCompareRows(cmp.data || []);
    } catch {}
  }, [API_BASE]);

  useEffect(() => {
    if (tab === 'ortho') reloadOrtho();
    if (tab === 'implants') reloadImplants();
    if (tab === 'perio') reloadPerio();
    if (tab === 'edu') reloadEdu();
    if (tab === 'referrals') reloadReferrals();
    if (tab === 'plans') reloadPlans();
    if (tab === 'branches') reloadMultiBranch();
  }, [tab, selPatient, reloadOrtho, reloadImplants, reloadPerio, reloadEdu, reloadReferrals, reloadPlans, reloadMultiBranch]);

  // ─── Ortho actions ───
  const createOrtho = async () => {
    if (!selPatient) return flash('error', 'Pick patient first');
    try { await axios.post(`${API_BASE}/ortho`, { ...newOrtho, patientId: selPatient._id, createdBy: currentUser?.username || 'admin' }); setNewOrtho({ caseType: 'aligners', brand: '', totalTrays: 0, estimatedCost: 0, chiefComplaint: '' }); flash('ok', 'Ortho case created'); reloadOrtho(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const addOrthoVisit = async (id) => {
    const tray = Number(prompt('Tray # worn?') || 0);
    const adj = prompt('Adjustments / notes?') || '';
    if (!tray && !adj) return;
    try { await axios.post(`${API_BASE}/ortho/${id}/visit`, { trayNumber: tray, adjustments: adj, by: currentUser?.username || 'admin' }); reloadOrtho(); }
    catch (e) { flash('error', e.message); }
  };

  // ─── Implants actions ───
  const createImplant = async () => {
    if (!selPatient) return flash('error', 'Pick patient first');
    if (!newImplant.toothNumber) return flash('error', 'Tooth required');
    try { await axios.post(`${API_BASE}/implants`, { ...newImplant, patientId: selPatient._id }); setNewImplant({ toothNumber: '', brand: '', diameter: 4.0, length: 10, lotNumber: '', surgeryDate: '', warrantyYears: 10 }); flash('ok', 'Implant recorded'); reloadImplants(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const advanceImplantStatus = async (id, status) => {
    try { await axios.put(`${API_BASE}/implants/${id}`, { status }); reloadImplants(); }
    catch (e) { flash('error', e.message); }
  };

  // ─── Perio actions ───
  const startPerioChart = () => {
    if (!selPatient) return flash('error', 'Pick patient first');
    const teeth = [...FDI_UPPER, ...FDI_LOWER].map(t => ({
      toothNumber: t, missing: false, mobility: 0, furcation: 0,
      sites: ['MB','B','DB','ML','L','DL'].map(s => ({ site: s, pd: 0, bop: false, plaque: false })),
    }));
    setPerioDraft({ patientId: selPatient._id, examBy: currentUser?.username || 'admin', examDate: new Date().toISOString().slice(0,10), classification: '', grade: '', teeth, notes: '' });
  };
  const updateSite = (toothIdx, siteIdx, field, value) => {
    setPerioDraft(p => {
      const d = JSON.parse(JSON.stringify(p));
      d.teeth[toothIdx].sites[siteIdx][field] = value;
      return d;
    });
  };
  const savePerio = async () => {
    if (!perioDraft) return;
    try { await axios.post(`${API_BASE}/perio`, perioDraft); setPerioDraft(null); flash('ok', 'Perio chart saved'); reloadPerio(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };

  // ─── Education actions ───
  const createEdu = async () => {
    if (!newEdu.title) return flash('error', 'Title required');
    try { await axios.post(`${API_BASE}/education`, newEdu); setNewEdu({ title: '', category: 'general', mediaType: 'link', externalUrl: '', forTreatment: '' }); flash('ok', 'Added'); reloadEdu(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const sendEdu = async (id) => {
    if (!selPatient) return flash('error', 'Pick patient first');
    try { await axios.post(`${API_BASE}/education/${id}/send`, { patientId: selPatient._id, channel: 'whatsapp', by: currentUser?.username || 'admin' }); flash('ok', 'Sent log recorded'); reloadEdu(); }
    catch (e) { flash('error', e.message); }
  };

  // ─── Referrals actions ───
  const createRef = async () => {
    if (!selPatient) return flash('error', 'Pick patient first');
    try { await axios.post(`${API_BASE}/referrals`, { ...newRef, patientId: selPatient._id, createdBy: currentUser?.username || 'admin' }); setNewRef({ direction: 'outbound', specialistName: '', specialty: 'oral_surgery', reason: '', urgency: 'routine' }); flash('ok', 'Referral created'); reloadReferrals(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const advanceRefStatus = async (id, status) => {
    try { await axios.post(`${API_BASE}/referrals/${id}/status`, { status }); reloadReferrals(); }
    catch (e) { flash('error', e.message); }
  };

  // ─── Plans actions ───
  const createPlan = async () => {
    if (!selPatient) return flash('error', 'Pick patient first');
    try { const { data } = await axios.post(`${API_BASE}/treatment-plans`, { ...newPlan, patientId: selPatient._id, createdBy: currentUser?.username || 'admin' }); setNewPlan({ planName: '', chiefComplaint: '', diagnosis: '' }); flash('ok', 'Plan created'); setExpandedPlan(data._id); reloadPlans(); }
    catch (e) { flash('error', e.response?.data?.error || e.message); }
  };
  const addPhase = async (planId) => {
    const name = prompt('Phase name?', `Phase`); if (!name) return;
    try { await axios.post(`${API_BASE}/treatment-plans/${planId}/phase`, { name }); reloadPlans(); } catch (e) { flash('error', e.message); }
  };
  const addPhaseItem = async (planId, pi) => {
    const proc = prompt('Procedure?'); if (!proc) return;
    const tooth = prompt('Tooth (FDI, optional)') || '';
    const cost = Number(prompt('Unit cost ₹') || 0);
    try { await axios.post(`${API_BASE}/treatment-plans/${planId}/phase/${pi}/item`, { procedure: proc, toothNumber: tooth, unitCost: cost, quantity: 1 }); reloadPlans(); }
    catch (e) { flash('error', e.message); }
  };
  const planAction = async (planId, action) => {
    try { await axios.post(`${API_BASE}/treatment-plans/${planId}/${action}`); reloadPlans(); }
    catch (e) { flash('error', e.message); }
  };

  const ALL_TABS = [
    { key: 'ortho',     label: 'Orthodontic',  icon: Smile },
    { key: 'implants',  label: 'Implants',     icon: Zap },
    { key: 'perio',     label: 'Perio Chart',  icon: Activity },
    { key: 'edu',       label: 'Education',    icon: BookOpen },
    { key: 'referrals', label: 'Referrals',    icon: Share2 },
    { key: 'plans',     label: 'Treatment Plans', icon: ClipboardList },
    { key: 'branches',  label: 'Multi-Branch', icon: Building2 },
  ];
  // Inside PatientProfile, hide the cross-clinic Multi-Branch tab.
  const TABS = lockedPatient ? ALL_TABS.filter(t => t.key !== 'branches') : ALL_TABS;

  const PatientPicker = () => {
    if (lockedPatient) return null; // hidden inside PatientProfile — patient is already in scope
    return (
    <div style={{ ...cardStyle, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <strong style={{ fontSize: 13 }}>Patient:</strong>
        {selPatient ? (
          <span style={{ background: '#dbeafe', color: '#1e40af', padding: '4px 10px', borderRadius: 6, fontSize: 13 }}>
            {selPatient.name} · {selPatient.phone || selPatient.uhid} <X size={14} style={{ cursor: 'pointer', verticalAlign: 'middle' }} onClick={() => { setSelPatient(null); setPicker(''); }} />
          </span>
        ) : (
          <div style={{ position: 'relative', flex: 1, maxWidth: 400 }}>
            <input style={{ ...inputStyle, width: '100%' }} placeholder="Search patient by name / phone / UHID" value={picker} onChange={e => setPicker(e.target.value)} />
            {patientMatches.length > 0 && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: 6, zIndex: 10, maxHeight: 220, overflow: 'auto' }}>
                {patientMatches.map(p => (
                  <div key={p._id} style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}
                    onClick={() => { setSelPatient(p); setPicker(''); }}>
                    <strong>{p.name}</strong> · {p.phone || p.uhid}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
    );
  };

  return (
    <div style={lockedPatient ? {} : { padding: 24, background: '#f9fafb', minHeight: '100vh' }}>
      {!lockedPatient && (
        <div style={{ background: 'linear-gradient(135deg, #0891b2 0%, #16a34a 100%)', padding: 24, borderRadius: 12, color: 'white', marginBottom: 16 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>🦷 Clinical Excellence</h1>
          <p style={{ margin: '6px 0 0', fontSize: 13, opacity: 0.92 }}>Ortho · Implants · Perio · Education · Referrals · Treatment Plans · Multi-Branch</p>
        </div>
      )}

      {msg.text && (
        <div style={{ padding: 10, borderRadius: 8, marginBottom: 12, background: msg.type === 'error' ? '#fee2e2' : '#d1fae5', color: msg.type === 'error' ? '#991b1b' : '#065f46', fontSize: 13 }}>
          {msg.type === 'error' ? <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} /> : <CheckCircle2 size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />}
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(t => {
          const Ic = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              style={{ padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
                background: tab === t.key ? theme.primary : 'white', color: tab === t.key ? 'white' : '#374151', boxShadow: '0 1px 2px rgba(0,0,0,0.06)' }}>
              <Ic size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ORTHO */}
      {tab === 'ortho' && (
        <>
          <PatientPicker />
          <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
            <strong style={{ fontSize: 14 }}>New Ortho Case</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 8, marginTop: 10 }}>
              <select style={inputStyle} value={newOrtho.caseType} onChange={e => setNewOrtho({ ...newOrtho, caseType: e.target.value })}>
                {['aligners','fixed_metal','fixed_ceramic','lingual','retention'].map(x => <option key={x}>{x}</option>)}
              </select>
              <input style={inputStyle} placeholder="Brand (Invisalign, 3M…)" value={newOrtho.brand} onChange={e => setNewOrtho({ ...newOrtho, brand: e.target.value })} />
              <input style={inputStyle} type="number" placeholder="Total trays" value={newOrtho.totalTrays} onChange={e => setNewOrtho({ ...newOrtho, totalTrays: Number(e.target.value) })} />
              <input style={inputStyle} type="number" placeholder="Est cost ₹" value={newOrtho.estimatedCost} onChange={e => setNewOrtho({ ...newOrtho, estimatedCost: Number(e.target.value) })} />
              <input style={{ ...inputStyle, gridColumn: 'span 2' }} placeholder="Chief complaint" value={newOrtho.chiefComplaint} onChange={e => setNewOrtho({ ...newOrtho, chiefComplaint: e.target.value })} />
              <button style={{ ...btnStyle, background: theme.primary }} onClick={createOrtho}><Plus size={14} /> Create</button>
            </div>
          </div>
          {orthoCases.map(c => (
            <div key={c._id} style={{ ...cardStyle, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong>{c.patientName}</strong> · <span style={{ background: ORTHO_STATUS_COLOR[c.status], color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 10 }}>{c.status}</span>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{c.caseType} · {c.brand || '—'} · tray {c.currentTray}/{c.totalTrays} · {c.visits?.length || 0} visits · {inr(c.estimatedCost)}</div>
                  {c.chiefComplaint && <div style={{ fontSize: 11, color: '#374151', marginTop: 4 }}>CC: {c.chiefComplaint}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button style={{ ...btnStyle, background: theme.primary, fontSize: 11, padding: '4px 10px' }} onClick={() => addOrthoVisit(c._id)}>+ Visit</button>
                  <Trash2 size={14} style={{ cursor: 'pointer', color: '#dc2626' }} onClick={async () => { if (window.confirm('Delete?')) { await axios.delete(`${API_BASE}/ortho/${c._id}`); reloadOrtho(); } }} />
                </div>
              </div>
              {(c.visits || []).slice(-5).reverse().map(v => (
                <div key={v.visitNumber} style={{ fontSize: 11, color: '#6b7280', marginTop: 4, paddingLeft: 10, borderLeft: '2px solid #e5e7eb' }}>
                  V{v.visitNumber} · {fmtDate(v.date)} · tray {v.trayNumber || '—'} · {v.adjustments || '—'}
                </div>
              ))}
            </div>
          ))}
          {!orthoCases.length && <div style={{ color: '#6b7280', fontSize: 13 }}>No ortho cases.</div>}
        </>
      )}

      {/* IMPLANTS */}
      {tab === 'implants' && (
        <>
          <PatientPicker />
          <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
            <strong style={{ fontSize: 14 }}>New Implant Fixture</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 10 }}>
              <input style={inputStyle} placeholder="Tooth # (FDI)" value={newImplant.toothNumber} onChange={e => setNewImplant({ ...newImplant, toothNumber: e.target.value })} />
              <input style={inputStyle} placeholder="Brand" value={newImplant.brand} onChange={e => setNewImplant({ ...newImplant, brand: e.target.value })} />
              <input style={inputStyle} type="number" step="0.1" placeholder="Ø mm" value={newImplant.diameter} onChange={e => setNewImplant({ ...newImplant, diameter: Number(e.target.value) })} />
              <input style={inputStyle} type="number" step="0.5" placeholder="Length mm" value={newImplant.length} onChange={e => setNewImplant({ ...newImplant, length: Number(e.target.value) })} />
              <input style={inputStyle} placeholder="Lot #" value={newImplant.lotNumber} onChange={e => setNewImplant({ ...newImplant, lotNumber: e.target.value })} />
              <input style={inputStyle} type="date" value={newImplant.surgeryDate} onChange={e => setNewImplant({ ...newImplant, surgeryDate: e.target.value })} />
              <input style={inputStyle} type="number" placeholder="Warranty yrs" value={newImplant.warrantyYears} onChange={e => setNewImplant({ ...newImplant, warrantyYears: Number(e.target.value) })} />
              <button style={{ ...btnStyle, background: theme.primary }} onClick={createImplant}><Plus size={14} /> Save</button>
            </div>
          </div>
          <div style={{ overflowX: 'auto', ...cardStyle, padding: 12 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#f9fafb' }}>
                <th style={{ padding: 8, textAlign: 'left' }}>Patient</th>
                <th style={{ padding: 8 }}>Tooth</th>
                <th style={{ padding: 8 }}>Brand · Ø×L</th>
                <th style={{ padding: 8 }}>Lot</th>
                <th style={{ padding: 8 }}>Surgery</th>
                <th style={{ padding: 8 }}>Warranty</th>
                <th style={{ padding: 8 }}>Status</th>
                <th style={{ padding: 8 }}>Actions</th>
              </tr></thead>
              <tbody>
                {implants.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No implants recorded.</td></tr>}
                {implants.map(i => (
                  <tr key={i._id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: 8 }}><strong>{i.patientName}</strong></td>
                    <td style={{ padding: 8, textAlign: 'center' }}>{i.toothNumber}</td>
                    <td style={{ padding: 8 }}>{i.brand || '—'} · {i.diameter}×{i.length}mm</td>
                    <td style={{ padding: 8, fontFamily: 'monospace', fontSize: 11 }}>{i.lotNumber || '—'}</td>
                    <td style={{ padding: 8 }}>{fmtDate(i.surgeryDate)}</td>
                    <td style={{ padding: 8, fontSize: 11 }}>{fmtDate(i.warrantyExpiresAt)}</td>
                    <td style={{ padding: 8 }}><span style={{ background: IMPLANT_STATUS_COLOR[i.status], color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 10 }}>{i.status}</span></td>
                    <td style={{ padding: 8 }}>
                      <select style={{ ...inputStyle, padding: '2px 4px', fontSize: 11 }} value={i.status} onChange={e => advanceImplantStatus(i._id, e.target.value)}>
                        {['planned','placed','integrated','loaded','failed','explanted'].map(s => <option key={s}>{s}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* PERIO */}
      {tab === 'perio' && (
        <>
          <PatientPicker />
          {!perioDraft && (
            <button style={{ ...btnStyle, background: theme.primary, marginBottom: 12 }} onClick={startPerioChart} disabled={!selPatient}>
              <Plus size={14} /> New perio exam
            </button>
          )}
          {perioDraft && (
            <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <input style={inputStyle} type="date" value={perioDraft.examDate} onChange={e => setPerioDraft({ ...perioDraft, examDate: e.target.value })} />
                <select style={inputStyle} value={perioDraft.classification} onChange={e => setPerioDraft({ ...perioDraft, classification: e.target.value })}>
                  <option value="">Classification</option>
                  {['healthy','gingivitis','stage_I','stage_II','stage_III','stage_IV'].map(x => <option key={x}>{x}</option>)}
                </select>
                <select style={inputStyle} value={perioDraft.grade} onChange={e => setPerioDraft({ ...perioDraft, grade: e.target.value })}>
                  <option value="">Grade</option>
                  {['A','B','C'].map(x => <option key={x}>{x}</option>)}
                </select>
                <button style={{ ...btnStyle, background: '#16a34a' }} onClick={savePerio}><CheckCircle2 size={14} /> Save</button>
                <button style={{ ...btnStyle, background: '#6b7280' }} onClick={() => setPerioDraft(null)}><X size={14} /> Cancel</button>
              </div>
              {/* Upper arch */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 10, minWidth: 900 }}>
                  <thead><tr>
                    <th></th>
                    {FDI_UPPER.map(t => <th key={t} colSpan={6} style={{ padding: 2, background: '#f3f4f6', borderLeft: '1px solid #e5e7eb' }}>{t}</th>)}
                  </tr>
                  <tr>
                    <th style={{ fontSize: 9 }}>PD</th>
                    {FDI_UPPER.flatMap(t => ['MB','B','DB','ML','L','DL'].map((s, si) => <th key={`${t}${s}`} style={{ padding: 1, fontSize: 8 }}>{s}</th>))}
                  </tr></thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: 2, fontSize: 9, fontWeight: 700 }}>PD</td>
                      {perioDraft.teeth.slice(0, 16).flatMap((t, ti) => t.sites.map((s, si) => (
                        <td key={`${ti}-${si}`} style={{ padding: 1 }}>
                          <input value={s.pd || ''} onChange={e => updateSite(ti, si, 'pd', Number(e.target.value) || 0)}
                            style={{ width: 22, textAlign: 'center', padding: 1, border: '1px solid #e5e7eb', background: s.pd >= 6 ? '#fee2e2' : s.pd >= 4 ? '#fef3c7' : 'white' }} />
                        </td>
                      )))}
                    </tr>
                    <tr>
                      <td style={{ padding: 2, fontSize: 9, fontWeight: 700 }}>BOP</td>
                      {perioDraft.teeth.slice(0, 16).flatMap((t, ti) => t.sites.map((s, si) => (
                        <td key={`b-${ti}-${si}`} style={{ padding: 1, textAlign: 'center' }}>
                          <input type="checkbox" checked={!!s.bop} onChange={e => updateSite(ti, si, 'bop', e.target.checked)} />
                        </td>
                      )))}
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Lower arch */}
              <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table style={{ borderCollapse: 'collapse', fontSize: 10, minWidth: 900 }}>
                  <thead><tr>
                    <th></th>
                    {FDI_LOWER.map(t => <th key={t} colSpan={6} style={{ padding: 2, background: '#f3f4f6', borderLeft: '1px solid #e5e7eb' }}>{t}</th>)}
                  </tr></thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: 2, fontSize: 9, fontWeight: 700 }}>PD</td>
                      {perioDraft.teeth.slice(16).flatMap((t, ti) => t.sites.map((s, si) => (
                        <td key={`L-${ti}-${si}`} style={{ padding: 1 }}>
                          <input value={s.pd || ''} onChange={e => updateSite(ti + 16, si, 'pd', Number(e.target.value) || 0)}
                            style={{ width: 22, textAlign: 'center', padding: 1, border: '1px solid #e5e7eb', background: s.pd >= 6 ? '#fee2e2' : s.pd >= 4 ? '#fef3c7' : 'white' }} />
                        </td>
                      )))}
                    </tr>
                    <tr>
                      <td style={{ padding: 2, fontSize: 9, fontWeight: 700 }}>BOP</td>
                      {perioDraft.teeth.slice(16).flatMap((t, ti) => t.sites.map((s, si) => (
                        <td key={`Lb-${ti}-${si}`} style={{ padding: 1, textAlign: 'center' }}>
                          <input type="checkbox" checked={!!s.bop} onChange={e => updateSite(ti + 16, si, 'bop', e.target.checked)} />
                        </td>
                      )))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: 10, color: '#6b7280', marginTop: 8 }}>Red = PD ≥6mm · Yellow = PD ≥4mm · Save auto-computes BOP%, avg PD, site counts</p>
            </div>
          )}

          {perioCharts.map(c => (
            <div key={c._id} style={{ ...cardStyle, padding: 12, marginBottom: 8 }}>
              <strong>{c.patientName}</strong> · {fmtDate(c.examDate)} · {c.classification || '—'} {c.grade && `Grade ${c.grade}`}
              <div style={{ display: 'flex', gap: 10, fontSize: 11, color: '#6b7280', marginTop: 4 }}>
                <span>BOP: <strong style={{ color: c.bopPercent >= 30 ? '#dc2626' : c.bopPercent >= 15 ? '#f59e0b' : '#16a34a' }}>{c.bopPercent}%</strong></span>
                <span>Avg PD: {c.averagePD}mm</span>
                <span>≥4mm: {c.sitesAbove4mm}</span>
                <span>≥6mm: <strong style={{ color: '#dc2626' }}>{c.sitesAbove6mm}</strong></span>
                <span>Plaque: {c.plaqueIndex}%</span>
              </div>
            </div>
          ))}
          {!perioCharts.length && !perioDraft && <div style={{ color: '#6b7280', fontSize: 13 }}>No perio exams yet.</div>}
        </>
      )}

      {/* EDUCATION */}
      {tab === 'edu' && (
        <>
          <PatientPicker />
          <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
            <strong style={{ fontSize: 14 }}>Add Education Item</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginTop: 10 }}>
              <input style={inputStyle} placeholder="Title*" value={newEdu.title} onChange={e => setNewEdu({ ...newEdu, title: e.target.value })} />
              <select style={inputStyle} value={newEdu.category} onChange={e => setNewEdu({ ...newEdu, category: e.target.value })}>
                {['general','preventive','restorative','endo','perio','prostho','ortho','implant','pedo','surgery','cosmetic'].map(x => <option key={x}>{x}</option>)}
              </select>
              <select style={inputStyle} value={newEdu.mediaType} onChange={e => setNewEdu({ ...newEdu, mediaType: e.target.value })}>
                {['video','pdf','image','link'].map(x => <option key={x}>{x}</option>)}
              </select>
              <input style={{ ...inputStyle, gridColumn: 'span 2' }} placeholder="YouTube / URL" value={newEdu.externalUrl} onChange={e => setNewEdu({ ...newEdu, externalUrl: e.target.value })} />
              <input style={inputStyle} placeholder="For treatment" value={newEdu.forTreatment} onChange={e => setNewEdu({ ...newEdu, forTreatment: e.target.value })} />
              <button style={{ ...btnStyle, background: theme.primary }} onClick={createEdu}><Plus size={14} /> Add</button>
            </div>
            <p style={{ fontSize: 11, color: '#6b7280', marginTop: 8, marginBottom: 0 }}>
              File uploads go to <code>POST /api/education/upload</code> (multer). Supports videos/PDFs up to 200MB.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
            {eduItems.map(i => (
              <div key={i._id} style={{ ...cardStyle, padding: 12 }}>
                <strong>{i.title}</strong>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{i.category} · {i.mediaType} · sent {i.sentCount}×</div>
                {i.description && <div style={{ fontSize: 11, marginTop: 4 }}>{i.description}</div>}
                {i.externalUrl && <a href={i.externalUrl} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: theme.primary }}>Open →</a>}
                <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                  <button style={{ ...btnStyle, background: '#16a34a', fontSize: 11, padding: '4px 10px' }} onClick={() => sendEdu(i._id)}><Send size={11} /> Send to pt</button>
                  <Trash2 size={14} style={{ cursor: 'pointer', color: '#dc2626', marginTop: 5 }} onClick={async () => { if (window.confirm('Delete?')) { await axios.delete(`${API_BASE}/education/${i._id}`); reloadEdu(); } }} />
                </div>
              </div>
            ))}
            {!eduItems.length && <div style={{ color: '#6b7280', fontSize: 13 }}>Library is empty.</div>}
          </div>
        </>
      )}

      {/* REFERRALS */}
      {tab === 'referrals' && (
        <>
          <PatientPicker />
          {refStats && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
              <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #3b82f6' }}>
                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>OUTBOUND</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{refStats.byDirection.outbound?.total || 0}</div>
              </div>
              <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #16a34a' }}>
                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>INBOUND</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{refStats.byDirection.inbound?.total || 0}</div>
              </div>
              <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #f59e0b' }}>
                <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>REWARDS PAYABLE</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#f59e0b' }}>{inr(refStats.rewardPayable)}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>{refStats.rewardPayableCount} due</div>
              </div>
            </div>
          )}
          <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
            <strong style={{ fontSize: 14 }}>New Referral</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 8, marginTop: 10 }}>
              <select style={inputStyle} value={newRef.direction} onChange={e => setNewRef({ ...newRef, direction: e.target.value })}>
                <option value="outbound">Outbound (to specialist)</option>
                <option value="inbound">Inbound (from referrer)</option>
              </select>
              <input style={inputStyle} placeholder={newRef.direction === 'outbound' ? 'Specialist name' : 'Referred by'} value={newRef.specialistName} onChange={e => setNewRef({ ...newRef, specialistName: e.target.value })} />
              <select style={inputStyle} value={newRef.specialty} onChange={e => setNewRef({ ...newRef, specialty: e.target.value })}>
                {['oral_surgery','endodontics','periodontics','prosthodontics','orthodontics','pedodontics','implantology','oral_medicine','ent','other'].map(x => <option key={x}>{x}</option>)}
              </select>
              <select style={inputStyle} value={newRef.urgency} onChange={e => setNewRef({ ...newRef, urgency: e.target.value })}>
                {['routine','urgent','emergency'].map(x => <option key={x}>{x}</option>)}
              </select>
              <input style={{ ...inputStyle, gridColumn: 'span 2' }} placeholder="Reason" value={newRef.reason} onChange={e => setNewRef({ ...newRef, reason: e.target.value })} />
              <button style={{ ...btnStyle, background: theme.primary }} onClick={createRef}><Plus size={14} /> Create</button>
            </div>
          </div>
          {referrals.map(r => (
            <div key={r._id} style={{ ...cardStyle, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <strong>{r.patientName}</strong> · <span style={{ background: REFERRAL_STATUS_COLOR[r.status], color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 10 }}>{r.status}</span> · <span style={{ fontSize: 11, color: '#6b7280' }}>{r.direction}</span>
                  <div style={{ fontSize: 11, color: '#374151', marginTop: 4 }}>
                    {r.direction === 'outbound' ? `→ ${r.specialistName || '—'}` : `← ${r.referredByName || '—'}`}
                    {r.specialty && ` · ${r.specialty}`} · {r.urgency}
                  </div>
                  {r.reason && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{r.reason}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <select style={{ ...inputStyle, padding: '2px 4px', fontSize: 11 }} value={r.status} onChange={e => advanceRefStatus(r._id, e.target.value)}>
                    {['created','sent','accepted','scheduled','completed','declined','lost'].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
              </div>
            </div>
          ))}
          {!referrals.length && <div style={{ color: '#6b7280', fontSize: 13 }}>No referrals yet.</div>}
        </>
      )}

      {/* TREATMENT PLANS */}
      {tab === 'plans' && (
        <>
          <PatientPicker />
          <div style={{ ...cardStyle, padding: 14, marginBottom: 12 }}>
            <strong style={{ fontSize: 14 }}>New Treatment Plan</strong>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginTop: 10 }}>
              <input style={inputStyle} placeholder="Plan name" value={newPlan.planName} onChange={e => setNewPlan({ ...newPlan, planName: e.target.value })} />
              <input style={inputStyle} placeholder="Chief complaint" value={newPlan.chiefComplaint} onChange={e => setNewPlan({ ...newPlan, chiefComplaint: e.target.value })} />
              <input style={inputStyle} placeholder="Diagnosis" value={newPlan.diagnosis} onChange={e => setNewPlan({ ...newPlan, diagnosis: e.target.value })} />
              <button style={{ ...btnStyle, background: theme.primary }} onClick={createPlan}><Plus size={14} /> Create</button>
            </div>
          </div>
          {plans.map(p => (
            <div key={p._id} style={{ ...cardStyle, padding: 14, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <strong>{p.planName}</strong> · <span style={{ background: PLAN_STATUS_COLOR[p.status], color: 'white', padding: '2px 8px', borderRadius: 4, fontSize: 10 }}>{p.status}</span>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>{p.patientName} · CC: {p.chiefComplaint || '—'}</div>
                  <div style={{ fontSize: 13, color: '#16a34a', fontWeight: 700, marginTop: 4 }}>{inr(p.grandTotal)}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {p.status === 'draft' && <button style={{ ...btnStyle, background: '#3b82f6', fontSize: 11, padding: '4px 10px' }} onClick={() => planAction(p._id, 'present')}>Present</button>}
                  {p.status === 'presented' && <>
                    <button style={{ ...btnStyle, background: '#16a34a', fontSize: 11, padding: '4px 10px' }} onClick={() => planAction(p._id, 'accept')}>Accept</button>
                    <button style={{ ...btnStyle, background: '#dc2626', fontSize: 11, padding: '4px 10px' }} onClick={() => planAction(p._id, 'decline')}>Decline</button>
                  </>}
                  <button style={{ ...btnStyle, background: '#6b7280', fontSize: 11, padding: '4px 10px' }} onClick={() => setExpandedPlan(expandedPlan === p._id ? null : p._id)}>
                    {expandedPlan === p._id ? '▲' : '▼'}
                  </button>
                </div>
              </div>
              {expandedPlan === p._id && (
                <div style={{ marginTop: 10, borderTop: '1px solid #e5e7eb', paddingTop: 10 }}>
                  {(p.phases || []).map((ph, pi) => (
                    <div key={pi} style={{ marginBottom: 10, paddingLeft: 10, borderLeft: '3px solid #3b82f6' }}>
                      <strong style={{ fontSize: 12 }}>Phase {ph.phaseNumber}: {ph.name}</strong>
                      <button style={{ ...btnStyle, background: theme.primary, fontSize: 10, padding: '2px 8px', marginLeft: 8 }} onClick={() => addPhaseItem(p._id, pi)}>+ Item</button>
                      {(ph.items || []).map((it, ii) => (
                        <div key={ii} style={{ fontSize: 11, marginTop: 4, padding: 6, background: it.status === 'done' ? '#d1fae5' : '#f9fafb', borderRadius: 4, display: 'flex', justifyContent: 'space-between' }}>
                          <span>{it.toothNumber && `#${it.toothNumber} · `}{it.procedure} · {inr(it.total)}</span>
                          <span>
                            {it.status === 'done' ? <CheckCircle2 size={12} style={{ color: '#16a34a' }} /> :
                              <button style={{ ...btnStyle, background: '#16a34a', fontSize: 9, padding: '1px 6px' }} onClick={async () => { await axios.post(`${API_BASE}/treatment-plans/${p._id}/item-done`, { phase: pi, item: ii, by: currentUser?.username || 'admin' }); reloadPlans(); }}>Done</button>}
                          </span>
                        </div>
                      ))}
                    </div>
                  ))}
                  <button style={{ ...btnStyle, background: '#6d28d9', fontSize: 11, padding: '4px 10px' }} onClick={() => addPhase(p._id)}>+ Add Phase</button>
                </div>
              )}
            </div>
          ))}
          {!plans.length && <div style={{ color: '#6b7280', fontSize: 13 }}>No treatment plans yet.</div>}
        </>
      )}

      {/* MULTI-BRANCH */}
      {tab === 'branches' && consolidation && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 12 }}>
            <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #3b82f6' }}>
              <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>TOTAL PATIENTS</div>
              <div style={{ fontSize: 22, fontWeight: 800 }}>{consolidation.totals.patients}</div>
            </div>
            <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #16a34a' }}>
              <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>NEW (30d)</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>{consolidation.totals.newPatients}</div>
            </div>
            <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #f59e0b' }}>
              <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>APPTS (30d)</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#f59e0b' }}>{consolidation.totals.appointments}</div>
            </div>
            <div style={{ ...cardStyle, padding: 12, borderLeft: '4px solid #16a34a' }}>
              <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 700 }}>REVENUE</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: '#16a34a' }}>{inr(consolidation.totals.revenue)}</div>
            </div>
          </div>
          <div style={{ ...cardStyle, padding: 12 }}>
            <strong style={{ fontSize: 13 }}>Per-branch comparison (last 30d)</strong>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 8 }}>
                <thead><tr style={{ background: '#f9fafb' }}>
                  <th style={{ padding: 8, textAlign: 'left' }}>Branch</th>
                  <th style={{ padding: 8 }}>City</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Patients</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>New</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Appts</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>No-show %</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Revenue</th>
                  <th style={{ padding: 8, textAlign: 'right' }}>Avg ticket</th>
                </tr></thead>
                <tbody>
                  {compareRows.length === 0 && <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#6b7280' }}>No branches configured.</td></tr>}
                  {compareRows.map(r => (
                    <tr key={r.branchId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: 8 }}><strong>{r.name}</strong></td>
                      <td style={{ padding: 8 }}>{r.city || '—'}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{r.patients}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{r.newPatients}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{r.appointments}</td>
                      <td style={{ padding: 8, textAlign: 'right', color: r.noShowPct >= 15 ? '#dc2626' : '#16a34a' }}>{r.noShowPct}%</td>
                      <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{inr(r.revenue)}</td>
                      <td style={{ padding: 8, textAlign: 'right' }}>{inr(r.avgTicket)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ClinicalPage;
